import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useListCarrierAddOnServices,
  useUpdateCarrierAddOnService,
  type UpdateCarrierAddOnServiceInput,
  type InsuranceServiceInput,
} from "@/api/carrier-add-on-services";
import { useGetCarriers } from "@/api/carriers";
import type { CarrierAddOnService } from "@/api.types.v2";
import { ServiceEnablementPanel } from "./service-enablement-panel";

/**
 * Tabbed detail view for a single INSURANCE add-on service. Three tabs:
 * Overview (code/name/active/coverage max/currency), Pricing (inline insurance
 * pricing form), Enablement (reuses ServiceEnablementPanel with type=INSURANCE).
 * Mirrors the structure of PickupServiceDetailScreen but without Rates +
 * Time Slots (PICKUP-only).
 */
export function InsuranceServiceDetailScreen() {
  const { id } = useParams<{ id: string }>();

  const { data: services, isLoading: servicesLoading } =
    useListCarrierAddOnServices({ type: "INSURANCE" });
  const { data: carriersResp } = useGetCarriers();

  const service = id ? services?.find((s) => s.id === id) : undefined;
  const providerName =
    carriersResp?.data.find((c) => c.id === service?.providerCarrierId)?.name ??
    "—";

  if (servicesLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="p-4 space-y-4">
        <div className="text-center p-8 text-muted-foreground">
          <p>Insurance service not found.</p>
          <Button asChild variant="link" size="sm" className="mt-2">
            <Link to="/add-on-services/insurance">
              <ArrowLeft className="h-3 w-3 mr-1" />
              Back to Insurance Services
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <DetailHeader service={service} providerName={providerName} />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="enablement">Enablement</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab service={service} />
        </TabsContent>
        <TabsContent value="pricing">
          <PricingTab service={service} />
        </TabsContent>
        <TabsContent value="enablement">
          <ServiceEnablementPanel serviceId={service.id} type="INSURANCE" />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DetailHeader({
  service,
  providerName,
}: {
  service: CarrierAddOnService;
  providerName: string;
}) {
  return (
    <div className="space-y-2">
      <div className="text-sm">
        <Link
          to="/add-on-services/insurance"
          className="text-muted-foreground hover:text-foreground inline-flex items-center"
        >
          <ArrowLeft className="h-3 w-3 mr-1" />
          Insurance Services
        </Link>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold">{service.name}</h1>
          <code className="font-mono text-sm text-muted-foreground">
            {service.code}
          </code>
          {service.active ? (
            <Badge
              variant="secondary"
              className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
            >
              Active
            </Badge>
          ) : (
            <Badge
              variant="secondary"
              className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100"
            >
              Inactive
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          Provider: <span className="text-foreground">{providerName}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers — dollars↔cents conversion + dirty-check parsing. Mirror the
// approach taken by the pickup Overview tab: parse on Save, surface validation
// toasts, leave Save enabled for dirty-but-invalid input.
// ---------------------------------------------------------------------------

// Parse a dollars input field. Returns:
//   - null  when the field is blank (means "clear the cents value")
//   - undefined on invalid input (negative or non-numeric)
//   - cents integer otherwise (rounded)
function parseDollarsToCents(s: string): number | null | undefined {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100);
}

// Same for percentage rate — accept blank → null, non-negative number otherwise.
function parsePercentage(s: string): number | null | undefined {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

// cents → dollar string for display in the input.
function centsToDollarString(cents: number | null | undefined): string {
  if (cents == null) return "";
  return String(cents / 100);
}

// ---------------------------------------------------------------------------
// Overview tab — code/name/active/coverage max/currency.
// ---------------------------------------------------------------------------
function OverviewTab({ service }: { service: CarrierAddOnService }) {
  const initialCoverage = service.insuranceDetails?.coverageMaxCents ?? null;
  const initialCurrency = service.insuranceDetails?.currencyCode ?? "CAD";

  const [name, setName] = useState(service.name);
  const [active, setActive] = useState(service.active);
  const [coverageMaxDollars, setCoverageMaxDollars] = useState(
    centsToDollarString(initialCoverage)
  );
  const [currencyCode, setCurrencyCode] = useState(initialCurrency);

  // Re-seed only on :id change — same pattern as pickup Overview tab. Avoids
  // overwriting in-flight edits when a background refetch lands.
  const serviceRef = useRef(service);
  serviceRef.current = service;
  useEffect(() => {
    const s = serviceRef.current;
    setName(s.name);
    setActive(s.active);
    setCoverageMaxDollars(
      centsToDollarString(s.insuranceDetails?.coverageMaxCents ?? null)
    );
    setCurrencyCode(s.insuranceDetails?.currencyCode ?? "CAD");
  }, [service.id]);

  const update = useUpdateCarrierAddOnService();

  // Build the partial patch. `null` → nothing changed, `undefined` → invalid.
  const buildPatch = (): UpdateCarrierAddOnServiceInput | null | undefined => {
    const patch: UpdateCarrierAddOnServiceInput = {};
    const trimmedName = name.trim();
    if (trimmedName && trimmedName !== service.name) patch.name = trimmedName;
    if (active !== service.active) patch.active = active;

    const insurancePatch: InsuranceServiceInput = {};
    let insuranceDirty = false;

    const coverage = parseDollarsToCents(coverageMaxDollars);
    if (coverage === undefined) {
      toast.error("Coverage max must be a non-negative number or blank.");
      return undefined;
    }
    if (coverage !== initialCoverage) {
      insurancePatch.coverageMaxCents = coverage;
      insuranceDirty = true;
    }

    const trimmedCurrency = currencyCode.trim().toUpperCase();
    if (!trimmedCurrency) {
      toast.error("Currency code is required.");
      return undefined;
    }
    if (trimmedCurrency !== initialCurrency) {
      insurancePatch.currencyCode = trimmedCurrency;
      insuranceDirty = true;
    }

    if (insuranceDirty) patch.insurance = insurancePatch;

    return Object.keys(patch).length === 0 ? null : patch;
  };

  // Save-disabled dirty check. Invalid input counts as dirty-but-invalid so the
  // Save handler surfaces the validation toast — same trade-off as pickup.
  const hasChanges = (() => {
    if (name.trim() && name.trim() !== service.name) return true;
    if (active !== service.active) return true;
    const coverage = parseDollarsToCents(coverageMaxDollars);
    if (coverage === undefined) return true;
    if (coverage !== initialCoverage) return true;
    const trimmedCurrency = currencyCode.trim().toUpperCase();
    if (trimmedCurrency !== initialCurrency) return true;
    return false;
  })();

  const handleSave = () => {
    const patch = buildPatch();
    if (patch === undefined) return;
    if (patch === null) {
      toast.message("Nothing to update.");
      return;
    }
    update.mutate(
      { id: service.id, data: patch },
      {
        onSuccess: (updated) => {
          toast.success("Insurance service updated.");
          setName(updated.name);
          setActive(updated.active);
          setCoverageMaxDollars(
            centsToDollarString(
              updated.insuranceDetails?.coverageMaxCents ?? null
            )
          );
          setCurrencyCode(updated.insuranceDetails?.currencyCode ?? "CAD");
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to update service.");
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Overview</CardTitle>
        <p className="text-sm text-muted-foreground">
          Code <code className="font-mono">{service.code}</code> and provider
          carrier can&apos;t be changed. To retire, toggle off the Active
          switch.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="is-detail-code">Code</Label>
          <Input
            id="is-detail-code"
            value={service.code}
            readOnly
            disabled
            className="font-mono"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="is-detail-name">Name</Label>
          <Input
            id="is-detail-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="is-detail-coverage">Coverage max (CAD)</Label>
            <Input
              id="is-detail-coverage"
              type="number"
              step="any"
              value={coverageMaxDollars}
              onChange={(e) => setCoverageMaxDollars(e.target.value)}
              placeholder="—"
            />
            <p className="text-xs text-muted-foreground">
              Upper bound on declared value covered by this tier.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="is-detail-currency">Currency code</Label>
            <Input
              id="is-detail-currency"
              value={currencyCode}
              onChange={(e) => setCurrencyCode(e.target.value)}
              placeholder="CAD"
              className="uppercase"
            />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label className="text-sm font-medium">Active</Label>
            <p className="text-xs text-muted-foreground">
              Inactive services are hidden from booking flows but preserved for
              existing references.
            </p>
          </div>
          <Button
            variant={active ? "outline" : "default"}
            size="sm"
            onClick={() => setActive(!active)}
          >
            {active ? (
              <>
                <X className="h-3 w-3 mr-1" />
                Deactivate
              </>
            ) : (
              <>
                <RotateCcw className="h-3 w-3 mr-1" />
                Reactivate
              </>
            )}
          </Button>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={update.isPending || !hasChanges}
          >
            <Save className="h-4 w-4 mr-1" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pricing tab — flat fee · percentage · min · max. Server upserts the details
// row; mutual exclusion (flat-fee XOR percentage+clamps) is NOT enforced
// here — server doesn't either (per Phase 1 plan).
// ---------------------------------------------------------------------------
function PricingTab({ service }: { service: CarrierAddOnService }) {
  const initialFlat = service.insuranceDetails?.flatFeeCents ?? null;
  const initialPct = service.insuranceDetails?.percentageRate ?? null;
  const initialMin = service.insuranceDetails?.minimumCents ?? null;
  const initialMax = service.insuranceDetails?.maximumCents ?? null;

  const [flatFeeDollars, setFlatFeeDollars] = useState(
    centsToDollarString(initialFlat)
  );
  const [percentageRate, setPercentageRate] = useState(
    initialPct != null ? String(initialPct) : ""
  );
  const [minimumDollars, setMinimumDollars] = useState(
    centsToDollarString(initialMin)
  );
  const [maximumDollars, setMaximumDollars] = useState(
    centsToDollarString(initialMax)
  );

  // Re-seed on :id change.
  const serviceRef = useRef(service);
  serviceRef.current = service;
  useEffect(() => {
    const d = serviceRef.current.insuranceDetails;
    setFlatFeeDollars(centsToDollarString(d?.flatFeeCents ?? null));
    setPercentageRate(
      d?.percentageRate != null ? String(d.percentageRate) : ""
    );
    setMinimumDollars(centsToDollarString(d?.minimumCents ?? null));
    setMaximumDollars(centsToDollarString(d?.maximumCents ?? null));
  }, [service.id]);

  const update = useUpdateCarrierAddOnService();

  const buildPatch = (): InsuranceServiceInput | null | undefined => {
    const patch: InsuranceServiceInput = {};

    const flat = parseDollarsToCents(flatFeeDollars);
    if (flat === undefined) {
      toast.error("Flat fee must be a non-negative number or blank.");
      return undefined;
    }
    if (flat !== initialFlat) patch.flatFeeCents = flat;

    const pct = parsePercentage(percentageRate);
    if (pct === undefined) {
      toast.error("Percentage rate must be a non-negative number or blank.");
      return undefined;
    }
    if (pct !== initialPct) patch.percentageRate = pct;

    const min = parseDollarsToCents(minimumDollars);
    if (min === undefined) {
      toast.error("Minimum must be a non-negative number or blank.");
      return undefined;
    }
    if (min !== initialMin) patch.minimumCents = min;

    const max = parseDollarsToCents(maximumDollars);
    if (max === undefined) {
      toast.error("Maximum must be a non-negative number or blank.");
      return undefined;
    }
    if (max !== initialMax) patch.maximumCents = max;

    return Object.keys(patch).length === 0 ? null : patch;
  };

  const hasChanges = (() => {
    const flat = parseDollarsToCents(flatFeeDollars);
    if (flat === undefined) return true;
    if (flat !== initialFlat) return true;
    const pct = parsePercentage(percentageRate);
    if (pct === undefined) return true;
    if (pct !== initialPct) return true;
    const min = parseDollarsToCents(minimumDollars);
    if (min === undefined) return true;
    if (min !== initialMin) return true;
    const max = parseDollarsToCents(maximumDollars);
    if (max === undefined) return true;
    if (max !== initialMax) return true;
    return false;
  })();

  const handleSave = () => {
    const insurancePatch = buildPatch();
    if (insurancePatch === undefined) return;
    if (insurancePatch === null) {
      toast.message("Nothing to update.");
      return;
    }
    update.mutate(
      { id: service.id, data: { insurance: insurancePatch } },
      {
        onSuccess: (updated) => {
          toast.success("Pricing updated.");
          const d = updated.insuranceDetails;
          setFlatFeeDollars(centsToDollarString(d?.flatFeeCents ?? null));
          setPercentageRate(
            d?.percentageRate != null ? String(d.percentageRate) : ""
          );
          setMinimumDollars(centsToDollarString(d?.minimumCents ?? null));
          setMaximumDollars(centsToDollarString(d?.maximumCents ?? null));
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to update pricing.");
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing</CardTitle>
        <p className="text-sm text-muted-foreground">
          Flat fee OR percentage-with-clamps. Mutual exclusion is not enforced —
          admin-discretion which pattern to use.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="ip-flat">Flat fee (CAD)</Label>
            <Input
              id="ip-flat"
              type="number"
              step="any"
              value={flatFeeDollars}
              onChange={(e) => setFlatFeeDollars(e.target.value)}
              placeholder="—"
            />
            <p className="text-xs text-muted-foreground">
              A fixed fee charged regardless of declared value.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ip-pct">Percentage rate (%)</Label>
            <Input
              id="ip-pct"
              type="number"
              step="any"
              value={percentageRate}
              onChange={(e) => setPercentageRate(e.target.value)}
              placeholder="—"
            />
            <p className="text-xs text-muted-foreground">
              e.g. <code>2.5</code> means 2.5% of declared value.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ip-min">Minimum (CAD)</Label>
            <Input
              id="ip-min"
              type="number"
              step="any"
              value={minimumDollars}
              onChange={(e) => setMinimumDollars(e.target.value)}
              placeholder="—"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ip-max">Maximum (CAD)</Label>
            <Input
              id="ip-max"
              type="number"
              step="any"
              value={maximumDollars}
              onChange={(e) => setMaximumDollars(e.target.value)}
              placeholder="—"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={update.isPending || !hasChanges}
          >
            <Save className="h-4 w-4 mr-1" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
